import { TitleCasePipe, JsonPipe, CommonModule } from '@angular/common';
import { Component, ElementRef, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormField, MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, map, Subscription } from 'rxjs';

@Component({
  selector: 'app-table-header',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule, MatButtonModule,
    MatFormField, MatDividerModule,
    MatExpansionModule, MatCheckboxModule, TitleCasePipe,
    MatTableModule,
    MatChipsModule,
    MatInputModule,
    MatMenuModule,
    MatIconModule,
    MatSelectModule,
    ReactiveFormsModule,
    JsonPipe,
  ],
  templateUrl: './table-header.component.html',
  styleUrl: './table-header.component.scss'
})
export class TableHeaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() patientsTableFilterArray!: Map<string, string[]>;
  @Input() patientsFiltersFormControlObject!: { [key: string]: FormGroup };
  @Input() searchBoxTitle?: string;
  @Input() hasData: boolean = true;
  @Input() showWhenSearching: boolean = false;
  @Input() searchableFields: { label: string; value: string }[] | null = null;
  @Input() selectedSearchField: string | null = null;

  @Output() filterChanged = new EventEmitter<{ filterKey: string; values: string[] }>();
  @Output() searchChanged = new EventEmitter<string>();
  @Output() searchFieldChanged = new EventEmitter<string | null>();
  @Output() searchCriteriaChanged = new EventEmitter<{ term: string; field: string | null }>();

  searchControl = new FormControl<string>('');
  searchFieldControl = new FormControl<string>('');
  filterKeys: string[] = [];
  private filterSubscriptions = new Subscription();
  private searchSubscriptions = new Subscription();
  private searchSubscriptionsInitialized = false;

  ngOnInit(): void {
    this.configureHeaderInputs();
    this.initializeSearchSubscriptions();
    this.emitSearchCriteria(this.searchControl.value ?? '', this.searchFieldControl.value ?? null);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['patientsTableFilterArray'] ||
      changes['patientsFiltersFormControlObject'] ||
      changes['searchableFields'] ||
      changes['selectedSearchField']
    ) {
      this.configureHeaderInputs();
    }
  }

  ngOnDestroy(): void {
    this.filterSubscriptions.unsubscribe();
    this.searchSubscriptions.unsubscribe();
  }

  private configureHeaderInputs(): void {
    if (this.patientsTableFilterArray instanceof Map) {
      this.filterKeys = Array.from(this.patientsTableFilterArray.keys());
    } else {
      this.filterKeys = [];
    }

    this.initializeSearchFieldControl();
    this.initializeFilterSubscriptions();
  }

  private initializeSearchSubscriptions(): void {
    if (this.searchSubscriptionsInitialized) {
      return;
    }
    this.searchSubscriptionsInitialized = true;

    this.searchSubscriptions.add(this.searchControl.valueChanges
      .pipe(
        debounceTime(350),
        map((value) => (value ?? '').trim()),
        distinctUntilChanged(),
        filter((value) => value === '' || value.length >= 3)
      )
      .subscribe((value) => {
        this.searchChanged.emit(value);
        this.emitSearchCriteria(value, this.searchFieldControl.value ?? null);
      }));

    this.searchSubscriptions.add(this.searchFieldControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value) => {
        this.searchFieldChanged.emit(value ?? null);
        this.emitSearchCriteria(this.searchControl.value ?? '', value ?? null);
      }));
  }

  private initializeFilterSubscriptions(): void {
    this.filterSubscriptions.unsubscribe();
    this.filterSubscriptions = new Subscription();
    Object.keys(this.patientsFiltersFormControlObject || {}).forEach((filterKey) => {
      const formGroup = this.patientsFiltersFormControlObject[filterKey];
      if (formGroup) {
        Object.keys(formGroup.controls).forEach((controlName) => {
          const control = formGroup.get(controlName);
          if (control) {
            this.filterSubscriptions.add(control.valueChanges.subscribe(() => {
              const selectedValues = this.getSelectedFiltersForKey(filterKey);
              this.filterChanged.emit({ filterKey, values: selectedValues });
            }));
          }
        });
      }
    });
  }

  private initializeSearchFieldControl(): void {
    if (!this.searchableFields?.length) {
      this.searchFieldControl.setValue('', { emitEvent: false });
      return;
    }
    const explicit = this.selectedSearchField;
    const fallback = this.searchableFields[0]?.value ?? '';
    this.searchFieldControl.setValue(explicit ?? fallback, { emitEvent: false });
  }

  private emitSearchCriteria(term: string, field: string | null): void {
    this.searchCriteriaChanged.emit({ term, field });
  }




  /**
   * Get all selected checkbox values for a specific filter key
   */
  private getSelectedFiltersForKey(filterKey: string): string[] {
    const formGroup = this.patientsFiltersFormControlObject[filterKey];
    if (!formGroup) return [];

    return Object.keys(formGroup.controls)
      .filter((controlName) => formGroup.get(controlName)?.value === true)
      .map((controlName) => controlName);
  }

  /**
   * Toggle expansion panel
   */
  expand(element: HTMLElement): void {
    element.classList.toggle('expand');
  }

  /**
   * Clear a specific filter
   */
  clearFilter(filterKey: string): void {
    const formGroup = this.patientsFiltersFormControlObject[filterKey];
    if (formGroup) {
      Object.keys(formGroup.controls).forEach((key) => {
        formGroup.get(key)?.setValue(false);
      });
    }
  }

  /**
   * Clear all filters
   */
  clearAllFilters(): void {
    this.searchControl.setValue('');
    Object.keys(this.patientsFiltersFormControlObject || {}).forEach((filterKey) => {
      this.clearFilter(filterKey);
    });
  }

  /**
   * Get all active filters for display
   */
  getActiveFilters(): { filterKey: string; values: string[] }[] {
    return this.filterKeys
      .map((key) => ({
        filterKey: key,
        values: this.getSelectedFiltersForKey(key)
      }))
      .filter((f) => f.values.length > 0);
  }

  hasAnyFilters(): boolean {
    return this.getActiveFilters().length > 0 || !!this.searchControl.value;
  }

  get displayHeader(): boolean {
    if (this.hasData) {
      return true;
    }
    if (!this.showWhenSearching) {
      return false;
    }
    return this.hasAnyFilters();
  }
}
